import { computed, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import {
  catchError,
  EMPTY,
  exhaustMap,
  filter,
  firstValueFrom,
  forkJoin,
  fromEvent,
  map,
  merge,
  mergeMap,
  of,
  pipe,
  switchMap,
  tap,
  timer,
} from 'rxjs';
import DataSource from 'devextreme/data/data_source';
import { DateTime } from 'luxon';
import { DxOdataStoreService } from '@shared/components/dx-data-grid/data-access';
import { APP_CONFIG } from '@application/config';
import {
  setPropError,
  setPropInit,
  setPropLoaded,
  setPropLoading,
  withoutForbiddenElevation,
  withReqState,
} from '@application/data-access';
import { ErrorStore } from '@application/errors/data-access';
import {
  AcademyLivesState,
  initialAcademyLivesState,
  LiveEventRecord,
} from './entities/academy-lives-store.model';
import { LiveIngestResponse } from './entities/academy-responses.model';
import { LiveEventEntity } from './entities/academy-entities.model';
import {
  LiveEventCreateRequest,
  LiveEventResponse,
  LiveEventUpdateRequest,
} from './entities/academy.model';
import { RecordingsQuery, ODataQuery } from './entities/academy-query.model';
import { LiveEventsService } from './infra/live-events.service';
import { LiveAttachmentsService } from './infra/live-attachments.service';
import { LiveSubscriptionsService } from './infra/live-subscriptions.service';
import { AcademyODataService } from './infra/academy-odata.service';
import { MineSubscriptionsQuery } from './entities/academy-query.model';

import { getDateTime } from '@shared/utils';

const ALL_OPS = [
  'loadAcervo',
  'list',
  'getById',
  'refreshById',
  'requestPlaybackToken',
  'createEvent',
  'assembleLiveEvent',
  'updateEvent',
  'setCover',
  'loadOne',
  'cancelEvent',
  'removeEvent',
  'loadMetrics',
  'loadAttachments',
  'addAttachment',
  'removeAttachment',
  'subscribe',
  'loadMySubscriptions',
] as const;

/**
 * The one query behind the shared live list (navbar indicator, live dock,
 * dashboard widgets, Academy home). `list` is a `switchMap` writing a single
 * `listIds` slot, so callers with different queries used to overwrite each
 * other's rows; with one fixed query the last response matches the first.
 *
 * - Status codes are numeric on the OData wire (Scheduled 1, Live 2, Recorded 4).
 * - `desc` keeps the live event right after the future scheduled ones, so the
 *   100 ceiling only drops it when more than 100 events are scheduled ahead.
 * - `select` is the union of the fields those consumers read. Never widen it
 *   with audience or streaming fields: the OData set does not hide them, and
 *   this list is loaded for every client tenant on every shell load.
 */
const LIVE_FEED_QUERY: ODataQuery = {
  filter:
    'LiveEventStatusType eq 1 or LiveEventStatusType eq 2 or LiveEventStatusType eq 4',
  orderby: 'ScheduledStartOnUtc desc',
  top: 100,
  select:
    'LiveEventId,Title,Description,ProductType,LiveEventStatusType,ScheduledStartOnUtc,CoverUrl',
};

/**
 * How often an open shell re-reads the live feed. The hub only talks to the `live:{id}`
 * group of whoever opened that live, so a browsing user learns a live went on air only by
 * reading the feed again. This runs for every client tenant, so it stays at a minute.
 */
const LIVE_FEED_REFRESH_MS = 60_000;

/**
 * Merge a same-typed batch into its by-id map. Safe within a single map because
 * both sides share the shape — acervo VMs never mix with OData entities here, so
 * private entity fields cannot bleed into the public acervo collection.
 */
function indexBy<T extends { LiveEventId: string }>(
  byId: Record<string, T>,
  items: T[]
): Record<string, T> {
  const next = { ...byId };
  for (const item of items) {
    next[item.LiveEventId] = { ...next[item.LiveEventId], ...item };
  }
  return next;
}

export const AcademyLivesStore = signalStore(
  { providedIn: 'root' },
  withState<AcademyLivesState>(initialAcademyLivesState),
  withReqState(...ALL_OPS),
  withComputed((store) => ({
    // Public recordings — read only from acervoById (pure VMs, no private fields).
    acervo: computed<LiveEventResponse[]>(() => {
      const byId = store.acervoById();
      return store
        .acervoIds()
        .map((id) => byId[id])
        .filter((e): e is LiveEventResponse => !!e);
    }),
    // Generic OData list — entities (the OData read shape is the intended view here).
    listedEvents: computed<LiveEventEntity[]>(() => {
      const byId = store.entityById();
      return store
        .listIds()
        .map((id) => byId[id])
        .filter((e): e is LiveEventEntity => !!e);
    }),
    // On-air events from the last `list()` — drives the home "Live ao vivo" deck + navbar dot.
    liveEvents: computed<LiveEventEntity[]>(() => {
      const byId = store.entityById();
      return store
        .listIds()
        .map((id) => byId[id])
        .filter(
          (e): e is LiveEventEntity => !!e && e.LiveEventStatusType === 'Live'
        );
    }),
    // Upcoming events, soonest first — drives "Outras lives" + "Inscreva-se" + navbar "A seguir".
    // A Scheduled event whose start is already behind us never went on air: it is not upcoming,
    // and sorted ascending it would sit in front of today's live.
    scheduledEvents: computed<LiveEventEntity[]>(() => {
      const byId = store.entityById();
      const now = DateTime.now().toMillis();
      return store
        .listIds()
        .map((id) => byId[id])
        .filter(
          (e): e is LiveEventEntity =>
            !!e &&
            e.LiveEventStatusType === 'Scheduled' &&
            (getDateTime(e.ScheduledStartOnUtc)?.toMillis() || 0) >= now
        )
        .sort(
          (a, b) =>
            (getDateTime(a.ScheduledStartOnUtc)?.toMillis() || 0) -
            (getDateTime(b.ScheduledStartOnUtc)?.toMillis() || 0)
        );
    }),
    // Focused detail — prefer the richer entity, fall back to the acervo VM.
    selectedEvent: computed<LiveEventRecord | null>(() => {
      const id = store.selectedId();
      if (!id) return null;
      return store.entityById()[id] ?? store.acervoById()[id] ?? null;
    }),
  })),
  withMethods(
    (
      store,
      liveEvents = inject(LiveEventsService),
      odata = inject(AcademyODataService),
      dxOdata = inject(DxOdataStoreService),
      attachments = inject(LiveAttachmentsService),
      subscriptions = inject(LiveSubscriptionsService),
      errorStore = inject(ErrorStore)
    ) => {
      const odataUrl = inject(APP_CONFIG).apiUrl.odata_academy_service;
      const document = inject(DOCUMENT);

      /**
       * Server-side `lib-dx-data-grid` DataSource over `GET /odata/LiveEvents` — paging
       * (`$top`/`$skip`), search/`$filter` and `$orderby` run on the full OData controller
       * (cf. `UserStore.getUsers`). Unlike the array `list()` path, enum fields arrive as
       * NUMERIC wire codes here (the DX `ODataStore` bypasses `AcademyODataService`'s
       * `fromWireEnums`), so producer grid cells map codes → labels themselves. Default
       * order is soonest-scheduled-first; the grid overrides it on header sort.
       */
      const getLivesDataSource = (params?: any): DataSource<LiveEventEntity> =>
        dxOdata.fromOdata<LiveEventEntity>({
          url: odataUrl,
          metadata: 'LiveEvents',
          key: 'LiveEventId',
          params: { $orderby: 'ScheduledStartOnUtc desc', ...params },
        });

      const loadAcervo = rxMethod<RecordingsQuery | void>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadAcervo'))),
          switchMap((query) =>
            liveEvents.recordings(query || {}).pipe(
              tapResponse({
                next: (items: LiveEventResponse[]) =>
                  patchState(
                    store,
                    {
                      acervoById: indexBy(store.acervoById(), items),
                      acervoIds: items.map((i) => i.LiveEventId),
                    },
                    setPropLoaded('loadAcervo')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadAcervo', error));
                },
              })
            )
          )
        )
      );

      const list = rxMethod<ODataQuery | void>(
        pipe(
          tap(() => patchState(store, setPropLoading('list'))),
          switchMap((query) =>
            odata.list<LiveEventEntity>('LiveEvents', query || {}).pipe(
              tapResponse({
                next: (page) =>
                  patchState(
                    store,
                    {
                      entityById: indexBy(store.entityById(), page.value),
                      listIds: page.value.map((i) => i.LiveEventId),
                    },
                    setPropLoaded('list')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('list', error));
                },
              })
            )
          )
        )
      );

      /** Load the shared live list with its fixed query — see `LIVE_FEED_QUERY`. */
      const loadLiveFeed = (): void => {
        list(LIVE_FEED_QUERY);
      };

      /**
       * Background read of the same feed. It leaves the `list` request state alone, or the
       * home and the decks would blink into their skeletons every tick, and a failed tick
       * keeps the last list and waits for the next one instead of raising a toast a minute.
       */
      const refreshLiveFeed = rxMethod<unknown>(
        pipe(
          filter(() => !document.hidden),
          exhaustMap(() =>
            odata.list<LiveEventEntity>('LiveEvents', LIVE_FEED_QUERY).pipe(
              tap((page) =>
                patchState(store, {
                  entityById: indexBy(store.entityById(), page.value),
                  listIds: page.value.map((i) => i.LiveEventId),
                })
              ),
              catchError(() => EMPTY)
            )
          )
        )
      );

      /**
       * Keep the feed fresh for as long as the caller lives: every `LIVE_FEED_REFRESH_MS`,
       * paused while the tab is hidden, and right away when the tab comes back. Call it from
       * an injection context (constructor) — that injector's destruction stops the timer, so
       * logout does not leave anonymous reads running.
       */
      const watchLiveFeed = (): void => {
        refreshLiveFeed(
          merge(
            timer(LIVE_FEED_REFRESH_MS, LIVE_FEED_REFRESH_MS),
            fromEvent(document, 'visibilitychange')
          )
        );
      };

      // OData exposes list only — read a single event with a `$filter`ed list
      // (`LiveEventId eq {id}`, top 1) rather than a non-existent `/{Set}({id})`.
      const getById = rxMethod<string>(
        pipe(
          tap((id) =>
            patchState(store, { selectedId: id }, setPropLoading('getById'))
          ),
          switchMap((id) =>
            odata
              .list<LiveEventEntity>('LiveEvents', {
                filter: `LiveEventId eq ${id}`,
                top: 1,
              })
              .pipe(
                tapResponse({
                  next: (page) => {
                    const entity = page.value[0];
                    patchState(
                      store,
                      entity
                        ? { entityById: indexBy(store.entityById(), [entity]) }
                        : {},
                      setPropLoaded('getById')
                    );
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, setPropError('getById', error));
                  },
                })
              )
          )
        )
      );

      // The same read as `getById`, for a page that is already showing the event and wants to
      // know whether it changed: the watch page asks once a minute, because a status push can
      // be lost and nothing else would tell. It runs with nobody having asked for anything, so
      // it moves no focus and raises nothing — no error alert, and no elevation modal on a 403.
      // A failure stays in `refreshByIdError`; the next read simply tries again.
      const refreshById = rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('refreshById'))),
          switchMap((id) =>
            odata
              .list<LiveEventEntity>(
                'LiveEvents',
                { filter: `LiveEventId eq ${id}`, top: 1 },
                withoutForbiddenElevation()
              )
              .pipe(
                tapResponse({
                  next: (page) => {
                    const entity = page.value[0];
                    patchState(
                      store,
                      entity
                        ? { entityById: indexBy(store.entityById(), [entity]) }
                        : {},
                      setPropLoaded('refreshById')
                    );
                  },
                  error: (error: HttpErrorResponse) =>
                    patchState(store, setPropError('refreshById', error)),
                })
              )
          )
        )
      );

      const requestPlaybackToken = rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('requestPlaybackToken'))),
          switchMap((liveEventId) =>
            liveEvents.playbackToken(liveEventId).pipe(
              tapResponse({
                next: (res) =>
                  patchState(
                    store,
                    {
                      playbackTokenByKey: {
                        ...store.playbackTokenByKey(),
                        [`event:${liveEventId}`]: res.Token,
                      },
                    },
                    setPropLoaded('requestPlaybackToken')
                  ),
                error: (error: HttpErrorResponse) => {
                  // A 403 means the viewer is outside the event's audience — an expected
                  // answer, not a failure to report. The dock, the widget and the home ask
                  // for this token with no user action behind them, so a red toast would
                  // fire on every shell load; the op error alone lets them hide the player.
                  // Same reading as `isAudienceDenied` in `academy-content.store.ts`.
                  if (error.status !== 403) {
                    errorStore.setHttpError(error);
                  }
                  patchState(
                    store,
                    setPropError('requestPlaybackToken', error)
                  );
                },
              })
            )
          )
        )
      );

      const createEvent = rxMethod<LiveEventCreateRequest>(
        pipe(
          tap(() => patchState(store, setPropLoading('createEvent'))),
          switchMap((vm) =>
            liveEvents.create(vm).pipe(
              tapResponse({
                next: (created) =>
                  patchState(
                    store,
                    { acervoById: indexBy(store.acervoById(), [created]) },
                    setPropLoaded('createEvent')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('createEvent', error));
                },
              })
            )
          )
        )
      );

      /**
       * Parallel multipart support-material uploads (`POST /LiveEvents/{id}/attachments`).
       * Mirrors `addAttachments$` in `AcademyLearningStore`: the N files are ONE inner
       * `forkJoin`, never N emissions into a `switchMap` (which would keep only the last).
       */
      const addAttachments$ = (liveEventId: string, files: File[]) =>
        files.length
          ? forkJoin(
              files.map((file) => attachments.addAttachment(liveEventId, file))
            )
          : of([]);

      /**
       * The steps of one assembly after `POST /LiveEvents` landed: the optional cover, then
       * every support material; emits the created event with the cover URL folded in.
       */
      const completeAssembly$ = (
        created: LiveEventResponse,
        cover: File | null,
        files: File[]
      ) =>
        (cover ? liveEvents.setCover(created.LiveEventId, cover) : of(null)).pipe(
          switchMap((coverRes) =>
            addAttachments$(created.LiveEventId, files).pipe(
              map(() =>
                coverRes?.CoverUrl
                  ? { ...created, CoverUrl: coverRes.CoverUrl }
                  : created
              )
            )
          )
        );

      /**
       * Schedule assembly (`agendar live`): ONE emission performs the whole chain —
       * `POST /LiveEvents` (Scheduled) → optional `POST /{id}/cover` →
       * `POST /{id}/attachments` per support material. Mirrors
       * `AcademyLearningStore.assembleCourse`; `assembleLiveEventLoaded` flips only after
       * every step lands. Being one emission, no step of a save cancels another step of the
       * SAME save. It does not hold across saves: the outer `switchMap` drops a previous
       * save's chain — uploads included — when `assembleLiveEvent` is called again while it
       * runs (the builder disables Cancel while saving; leaving by the sidebar or Back and
       * saving another live still aborts them, as in `assembleCourse`).
       *
       * Not atomic across calls: if the cover or an upload fails, the live already exists
       * (Scheduled, visible to its audience) with whatever uploads had succeeded. So the
       * created event is committed — cached in `acervoById`, id in `assembledEventId` — as
       * soon as `POST /LiveEvents` lands, before those steps. A later failure sets the op
       * error but keeps both, and the builder opens that live for editing instead of POSTing
       * a duplicate. (`assembleCourse` keeps the gap: its orphan is an unpublished Draft.)
       */
      const assembleLiveEvent = rxMethod<{
        event: LiveEventCreateRequest;
        cover: File | null;
        attachments: File[];
      }>(
        pipe(
          tap(() =>
            patchState(
              store,
              { assembledEventId: null },
              setPropLoading('assembleLiveEvent')
            )
          ),
          // `attachments` is renamed: the injected `LiveAttachmentsService` owns that name here.
          switchMap(({ event, cover, attachments: files }) =>
            liveEvents.create(event).pipe(
              // Commit the event the moment it exists: a cover/upload failure below must
              // not erase the fact that the live was created.
              tap((created) =>
                patchState(store, {
                  acervoById: indexBy(store.acervoById(), [created]),
                  assembledEventId: created.LiveEventId,
                })
              ),
              switchMap((created) => completeAssembly$(created, cover, files)),
              tapResponse({
                next: (created) =>
                  patchState(
                    store,
                    { acervoById: indexBy(store.acervoById(), [created]) },
                    setPropLoaded('assembleLiveEvent')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('assembleLiveEvent', error));
                },
              })
            )
          )
        )
      );

      /** `PATCH /LiveEvents?id={guid}` — save edited scheduling metadata (edit mode). */
      const updateEvent = rxMethod<{
        liveEventId: string;
        changes: LiveEventUpdateRequest;
      }>(
        pipe(
          tap(() => patchState(store, setPropLoading('updateEvent'))),
          switchMap(({ liveEventId, changes }) =>
            liveEvents.update(liveEventId, changes).pipe(
              tapResponse({
                next: (updated) =>
                  patchState(
                    store,
                    { acervoById: indexBy(store.acervoById(), [updated]) },
                    setPropLoaded('updateEvent')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('updateEvent', error));
                },
              })
            )
          )
        )
      );

      /**
       * `GET /LiveEvents?id={guid}` — single read that carries the full row (audience,
       * products, `CoverUrl`), so the edit builder prefills from it on a deep-link. The
       * shared list `$select`s only 7 columns, so it lacks the audience/product fields.
       * Mirrors `AcademyLearningStore.loadCourse`; result lands in `acervoById`.
       */
      const loadOne = rxMethod<string>(
        pipe(
          tap((id) =>
            patchState(store, { selectedId: id }, setPropLoading('loadOne'))
          ),
          switchMap((id) =>
            liveEvents.getById(id).pipe(
              tapResponse({
                next: (event) =>
                  patchState(
                    store,
                    event
                      ? { acervoById: indexBy(store.acervoById(), [event]) }
                      : {},
                    setPropLoaded('loadOne')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadOne', error));
                },
              })
            )
          )
        )
      );

      /** `POST /LiveEvents/{id}/cover` — replace the cover (edit mode, immediate on pick). */
      const setCover = rxMethod<{ liveEventId: string; file: File }>(
        pipe(
          tap(() => patchState(store, setPropLoading('setCover'))),
          mergeMap(({ liveEventId, file }) =>
            liveEvents.setCover(liveEventId, file).pipe(
              tapResponse({
                next: () => patchState(store, setPropLoaded('setCover')),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('setCover', error));
                },
              })
            )
          )
        )
      );

      const cancelEvent = rxMethod<{ liveEventId: string; reason?: string }>(
        pipe(
          tap(() => patchState(store, setPropLoading('cancelEvent'))),
          switchMap(({ liveEventId, reason }) =>
            liveEvents.cancel(liveEventId, reason).pipe(
              tapResponse({
                next: () => patchState(store, setPropLoaded('cancelEvent')),
                // ⚠ 409 (not cancellable) surfaces raw; the consumer maps the "conflict" copy.
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('cancelEvent', error));
                },
              })
            )
          )
        )
      );

      /**
       * `DELETE /LiveEvents?id={guid}` (soft delete). On success the id also leaves the
       * shared feed and the collection, so the navbar and the home stop pointing at it
       * before the next background refresh.
       */
      const removeEvent = rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('removeEvent'))),
          switchMap((liveEventId) =>
            liveEvents.remove(liveEventId).pipe(
              tapResponse({
                next: () =>
                  patchState(
                    store,
                    {
                      listIds: store.listIds().filter((id) => id !== liveEventId),
                      acervoIds: store
                        .acervoIds()
                        .filter((id) => id !== liveEventId),
                    },
                    setPropLoaded('removeEvent')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('removeEvent', error));
                },
              })
            )
          )
        )
      );

      const loadMetrics = rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadMetrics'))),
          switchMap((liveEventId) =>
            liveEvents.metrics(liveEventId).pipe(
              tapResponse({
                next: (metrics) =>
                  patchState(
                    store,
                    {
                      metricsByEvent: {
                        ...store.metricsByEvent(),
                        [liveEventId]: metrics,
                      },
                    },
                    setPropLoaded('loadMetrics')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadMetrics', error));
                },
              })
            )
          )
        )
      );

      /**
       * `GET /LiveEvents/{id}/attachments` — support materials for one event, as a
       * plain array. Gated by the event's app scope and audience. Since backend 012-C a
       * viewer outside them normally gets 404 (the visibility filter hides the event), and
       * 403 only when the event is visible but the gate still refuses. The service
       * suppresses the elevation modal: audience is tenant-scoped, so no supervisor login
       * could put the viewer inside it.
       */
      const loadAttachments = rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadAttachments'))),
          switchMap((liveEventId) =>
            attachments.listAttachments(liveEventId).pipe(
              tapResponse({
                next: (list) =>
                  patchState(
                    store,
                    {
                      attachmentsByEvent: {
                        ...store.attachmentsByEvent(),
                        [liveEventId]: list,
                      },
                    },
                    setPropLoaded('loadAttachments')
                  ),
                error: (error: HttpErrorResponse) => {
                  // Backend 012-C splits "not for you" in two: 404 when the event is
                  // missing, deleted or hidden from the caller by the visibility filter
                  // (the normal answer for a viewer outside the app scope or audience),
                  // and 403 when the event is visible but the app/audience gate refuses.
                  // Both mean "no material available to you", not a failure: this read
                  // runs on page load with no user action behind it, so resolve both as a
                  // legitimate EMPTY read — loaded, not error — and let the page show its
                  // empty state. Same reading as `isAudienceDenied` in
                  // `academy-content.store.ts`, widened to the 404 this endpoint now uses.
                  // Producer writes below keep the toast: a 403 there means "not the
                  // producer", a different axis.
                  if (error.status === 404 || error.status === 403) {
                    patchState(
                      store,
                      {
                        attachmentsByEvent: {
                          ...store.attachmentsByEvent(),
                          [liveEventId]: [],
                        },
                      },
                      setPropLoaded('loadAttachments')
                    );
                    return;
                  }
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadAttachments', error));
                },
              })
            )
          )
        )
      );

      /**
       * `POST /LiveEvents/{id}/attachments` — upload one support material (producer).
       * The descriptor comes back WITHOUT a `LiveEventId`, so the state is keyed by the
       * id that was asked for, never by a field on the response.
       */
      const addAttachment = rxMethod<{ liveEventId: string; file: File }>(
        pipe(
          tap(() => patchState(store, setPropLoading('addAttachment'))),
          // mergeMap (not switchMap): dropping N files fires N emissions — switchMap would
          // cancel all but the last, persisting only one attachment.
          mergeMap(({ liveEventId, file }) =>
            attachments.addAttachment(liveEventId, file).pipe(
              tapResponse({
                next: (created) =>
                  patchState(
                    store,
                    {
                      attachmentsByEvent: {
                        ...store.attachmentsByEvent(),
                        [liveEventId]: [
                          ...(store.attachmentsByEvent()[liveEventId] ?? []),
                          created,
                        ],
                      },
                    },
                    setPropLoaded('addAttachment')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('addAttachment', error));
                },
              })
            )
          )
        )
      );

      /**
       * `DELETE /LiveEvents/{liveEventId}/attachments/{attachmentId}` — remove one
       * support material (producer). `mergeMap` so removing two rows in quick
       * succession never cancels the first call.
       */
      const removeAttachment = rxMethod<{
        liveEventId: string;
        attachmentId: string;
      }>(
        pipe(
          tap(() => patchState(store, setPropLoading('removeAttachment'))),
          mergeMap(({ liveEventId, attachmentId }) =>
            attachments.removeAttachment(liveEventId, attachmentId).pipe(
              tapResponse({
                next: () =>
                  patchState(
                    store,
                    {
                      attachmentsByEvent: {
                        ...store.attachmentsByEvent(),
                        [liveEventId]: (
                          store.attachmentsByEvent()[liveEventId] ?? []
                        ).filter((a) => a.LiveAttachmentId !== attachmentId),
                      },
                    },
                    setPropLoaded('removeAttachment')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('removeAttachment', error));
                },
              })
            )
          )
        )
      );

      // Reminder bell on "Próximas lives" — subscribe/unsubscribe the current viewer.
      // Optimistic: flip `subscribedEventIds` immediately so the bell reacts on click,
      // then revert that one id if the round-trip fails. `mergeMap` (not `switchMap`) so
      // toggling different rows in quick succession never cancels an in-flight call.
      // ⚠ subscribing to an Ended/Cancelled/Recorded event 500s (see service); the home
      // only offers the bell on Scheduled rows, and the revert covers it regardless.
      const subscribeLive = rxMethod<string>(
        pipe(
          tap((id) =>
            patchState(
              store,
              {
                subscribedEventIds: [
                  ...new Set([...store.subscribedEventIds(), id]),
                ],
              },
              setPropLoading('subscribe')
            )
          ),
          mergeMap((id) =>
            subscriptions.subscribe(id).pipe(
              tapResponse({
                next: () => patchState(store, setPropLoaded('subscribe')),
                error: (error: HttpErrorResponse) => {
                  patchState(store, {
                    subscribedEventIds: store
                      .subscribedEventIds()
                      .filter((x) => x !== id),
                  });
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('subscribe', error));
                },
              })
            )
          )
        )
      );

      const unsubscribeLive = rxMethod<string>(
        pipe(
          tap((id) =>
            patchState(
              store,
              {
                subscribedEventIds: store
                  .subscribedEventIds()
                  .filter((x) => x !== id),
              },
              setPropLoading('subscribe')
            )
          ),
          mergeMap((id) =>
            subscriptions.unsubscribe(id).pipe(
              tapResponse({
                next: () => patchState(store, setPropLoaded('subscribe')),
                error: (error: HttpErrorResponse) => {
                  patchState(store, {
                    subscribedEventIds: [
                      ...new Set([...store.subscribedEventIds(), id]),
                    ],
                  });
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('subscribe', error));
                },
              })
            )
          )
        )
      );

      // Imperative (Promise) on purpose: the control room fetches credentials on entering
      // the ready phase (to display RTMPS server/key) and re-uses them for WHIP publish.
      // Credentials are publish secrets — never persisted beyond the component lifetime.
      // Errors (403/404/409) reject so the caller can fall back gracefully.
      const requestIngestCredentials = (
        liveEventId: string
      ): Promise<LiveIngestResponse> =>
        firstValueFrom(liveEvents.ingest(liveEventId));

      /**
       * `GET /LiveSubscriptions/mine` — server truth for the student's saved lives.
       * Also seeds `subscribedEventIds` (union with any optimistic toggles already
       * applied) so the reminder bell reflects server truth on first paint while
       * `subscribeLive`/`unsubscribeLive` keep mutating that same single source.
       */
      const loadMySubscriptions = rxMethod<MineSubscriptionsQuery | void>(
        pipe(
          tap(() => patchState(store, setPropLoading('loadMySubscriptions'))),
          switchMap((query) =>
            subscriptions.mine(query || {}).pipe(
              tapResponse({
                next: (res) =>
                  patchState(
                    store,
                    {
                      mySubscriptions: res.items ?? [],
                      mySubscriptionsTotal: res.total ?? 0,
                      subscribedEventIds: [
                        ...new Set([
                          ...store.subscribedEventIds(),
                          ...(res.items ?? []).map((s) => s.LiveEventId),
                        ]),
                      ],
                    },
                    setPropLoaded('loadMySubscriptions')
                  ),
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error);
                  patchState(store, setPropError('loadMySubscriptions', error));
                },
              })
            )
          )
        )
      );

      const selectEvent = (id: string | null): void =>
        patchState(store, { selectedId: id });
      const reset = (): void =>
        patchState(store, initialAcademyLivesState, setPropInit(...ALL_OPS));
      /**
       * Clear a previous visit's builder ops: a stale assembly/update Loaded would navigate
       * away at once, a stale `assembledEventId` would redirect a fresh form to an old live,
       * and a stale cover or attachment add/remove Loaded (e.g. left by the recording-attach
       * page) would pop its success toast on open. `setCover` belongs here for exactly that
       * reason: the page binds a success alert to `setCoverLoaded` and the effect fires on its
       * first run when the flag is already true, so a cover swapped on the previous live
       * toasted again on the next form.
       */
      const resetAssemble = (): void =>
        patchState(
          store,
          { assembledEventId: null },
          setPropInit(
            'assembleLiveEvent',
            'updateEvent',
            'setCover',
            'addAttachment',
            'removeAttachment'
          )
        );
      /**
       * Clear a previous page's attachment write ops. Pages that toast on
       * `addAttachmentLoaded`/`removeAttachmentLoaded` call it on open: the alert effect fires
       * on its first run when the flag is already true, so a leftover Loaded toasts falsely.
       */
      const resetAttachmentWrites = (): void =>
        patchState(store, setPropInit('addAttachment', 'removeAttachment'));

      /** Same reason as `resetAttachmentWrites`, for the page that toasts on `removeEventLoaded`. */
      const resetRemoveEvent = (): void =>
        patchState(store, setPropInit('removeEvent'));

      return {
        getLivesDataSource,
        loadAcervo,
        list,
        loadLiveFeed,
        watchLiveFeed,
        getById,
        refreshById,
        requestPlaybackToken,
        createEvent,
        assembleLiveEvent,
        updateEvent,
        setCover,
        loadOne,
        resetAssemble,
        resetAttachmentWrites,
        resetRemoveEvent,
        cancelEvent,
        removeEvent,
        loadMetrics,
        loadAttachments,
        addAttachment,
        removeAttachment,
        subscribeLive,
        unsubscribeLive,
        requestIngestCredentials,
        loadMySubscriptions,
        selectEvent,
        reset,
      };
    }
  )
);
